import { useState, useCallback, useEffect } from 'react';
import { SchemaProperty } from './useRequestSchema';

export interface UseResponseSchemaReturn {
  // State
  responseSchemaProperties: SchemaProperty[];
  setResponseSchemaProperties: React.Dispatch<React.SetStateAction<SchemaProperty[]>>;
  responseSchemaJson: string;
  setResponseSchemaJson: React.Dispatch<React.SetStateAction<string>>;
  responseSchemaJsonValid: boolean;
  responseSchemaValidationErrors: string[];
  showResponseJson: boolean;
  setShowResponseJson: React.Dispatch<React.SetStateAction<boolean>>;
  responseMetadataSchemaProperties: SchemaProperty[];
  setResponseMetadataSchemaProperties: React.Dispatch<React.SetStateAction<SchemaProperty[]>>;
  responseMetadataSchemaJson: string;
  setResponseMetadataSchemaJson: React.Dispatch<React.SetStateAction<string>>;
  responseMetadataJsonValid: boolean;
  showResponseMetadataJson: boolean;
  setShowResponseMetadataJson: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Response schema property functions
  toggleResponsePropertyCollapsed: (index: number) => void;
  isResponseChildVisible: (childProperty: SchemaProperty, parentIndex?: number, properties?: SchemaProperty[]) => boolean;
  addResponseSchemaProperty: () => void;
  addResponseChildProperty: (parentIndex: number) => void;
  removeResponseProperty: (index: number) => void;
  updateResponsePropertyName: (index: number, name: string) => void;
  updateResponsePropertyType: (index: number, type: string) => void;
  updateResponsePropertyRequired: (index: number, required: boolean) => void;
  updateResponsePropertyXIdentity: (index: number, xIdentity: boolean) => void;
  updateResponseValidationRuleType: (index: number, ruleType: string) => void;
  updateResponseValidationRuleValue: (index: number, value: string) => void;
  updateResponseValidationRuleElementType: (index: number, elementType: string) => void;
  updateResponseValidationRuleValues: (index: number, values: string) => void;
  updateResponseValidationRuleMin: (index: number, min: string) => void;
  updateResponseValidationRuleMax: (index: number, max: string) => void;
  updateResponseSchemaJsonFromProperties: (properties?: SchemaProperty[]) => void;
  updateResponseSchemaJson: (properties?: SchemaProperty[]) => void;
  parseResponseSchemaPreservingCollapsedState: (schemaText: string) => SchemaProperty[];
  expandAllResponseProperties: () => void;
  collapseAllResponseProperties: () => void;
  syncResponseVisualToJson: () => void;
  syncResponseJsonToVisual: () => void;
  syncResponseVisualWithJson: () => void;
  
  // Response metadata functions
  addResponseMetadataProperty: () => void;
  addResponseMetadataChildProperty: (parentIndex: number) => void;
  removeResponseMetadataProperty: (index: number) => void;
  updateResponseMetadataPropertyName: (index: number, name: string) => void;
  updateResponseMetadataPropertyType: (index: number, type: string) => void;
  updateResponseMetadataPropertyValue: (index: number, value: string) => void;
  toggleResponseMetadataPropertyCollapsed: (index: number) => void;
  isMetadataChildVisible: (property: SchemaProperty, parentIndex?: number) => boolean;
  updateResponseMetadataJsonFromProperties: (properties?: SchemaProperty[]) => void;
  syncResponseMetadataVisualToJson: () => void;
  syncResponseMetadataJsonToVisual: () => void;
  
  // Shared utilities
  parseValueType: (value: string) => { type: string; parsedValue: any };
  parseSchemaToProperties: (schemaText: string) => SchemaProperty[];
  buildResponseSchemaFromProperties: (properties?: SchemaProperty[]) => any;
}

export const useResponseSchema = (
  setIsModified: (modified: boolean) => void,
  parseSchemaToPropertiesFn: (schemaText: string) => SchemaProperty[]
): UseResponseSchemaReturn => {
  // Response schema state
  const [responseSchemaProperties, setResponseSchemaProperties] = useState<SchemaProperty[]>([]);
  const [responseSchemaJson, setResponseSchemaJson] = useState<string>('');
  const [responseSchemaJsonValid, setResponseSchemaJsonValid] = useState(true);
  const [responseSchemaValidationErrors, setResponseSchemaValidationErrors] = useState<string[]>([]);
  const [showResponseJson, setShowResponseJson] = useState(true);
  
  // Response metadata state
  const [responseMetadataSchemaProperties, setResponseMetadataSchemaProperties] = useState<SchemaProperty[]>([]);
  const [responseMetadataSchemaJson, setResponseMetadataSchemaJson] = useState<string>('{}');
  const [responseMetadataJsonValid, setResponseMetadataJsonValid] = useState(true);
  const [showResponseMetadataJson, setShowResponseMetadataJson] = useState(true);

  // Validate response schema JSON whenever it changes
  useEffect(() => {
    // Skip validation for empty schemas
    if (!responseSchemaJson.trim()) {
      setResponseSchemaJsonValid(true);
      setResponseSchemaValidationErrors([]);
      return;
    }

    try {
      const parsed = JSON.parse(responseSchemaJson);
      
      // Validate it's an object
      if (typeof parsed !== 'object' || parsed === null) {
        setResponseSchemaJsonValid(false);
        setResponseSchemaValidationErrors(['Response schema must be a JSON object']);
        return;
      }
      
      // Validate basic JSON Schema structure
      if (parsed.type && typeof parsed.type !== 'string') {
        setResponseSchemaJsonValid(false);
        setResponseSchemaValidationErrors(['Schema "type" field must be a string']);
        return;
      }
      
      // If it has properties, validate they're in an object
      if (parsed.properties && typeof parsed.properties !== 'object') {
        setResponseSchemaJsonValid(false);
        setResponseSchemaValidationErrors(['Schema "properties" field must be an object']);
        return;
      }
      
      // If it has required, validate it's an array
      if (parsed.required && !Array.isArray(parsed.required)) {
        setResponseSchemaJsonValid(false);
        setResponseSchemaValidationErrors(['Schema "required" field must be an array']);
        return;
      }
      
      // All validations passed
      setResponseSchemaJsonValid(true);
      setResponseSchemaValidationErrors([]);
    } catch (error: any) {
      setResponseSchemaJsonValid(false);
      setResponseSchemaValidationErrors([`Invalid JSON: ${error.message}`]);
    }
  }, [responseSchemaJson]);

  // Response schema property functions
  const toggleResponsePropertyCollapsed = useCallback((index: number) => {
    setResponseSchemaProperties(prev => {
      const newProperties = [...prev];
      newProperties[index].collapsed = !newProperties[index].collapsed;
      return newProperties;
    });
  }, []);

  const isResponseChildVisible = useCallback((childProperty: SchemaProperty, parentIndex?: number, properties?: SchemaProperty[]): boolean => {
    if (parentIndex === undefined) return true;
    
    const propertiesArray = properties || responseSchemaProperties;
    const parent = propertiesArray[parentIndex];
    if (!parent) return false;
    
    if (parent.collapsed) return false;
    
    if (parent.parent !== undefined) {
      return isResponseChildVisible(parent, parent.parent, propertiesArray);
    }
    
    return true;
  }, [responseSchemaProperties]);

  const addResponseSchemaProperty = useCallback(() => {
    const newProperty: SchemaProperty = {
      name: '',
      type: 'string',
      required: false,
      xIdentity: false,
      level: 0,
      collapsed: true
    };
    setResponseSchemaProperties(prev => [...prev, newProperty]);
    setIsModified(true);
  }, [setIsModified]);

  const addResponseChildProperty = useCallback((parentIndex: number) => {
    setResponseSchemaProperties(prev => {
      const buildPath = (index: number): string[] => {
        const path: string[] = [];
        let current: number | undefined = index;
        
        while (current !== undefined) {
          const prop: SchemaProperty = prev[current];
          if (prop.name) {
            path.unshift(prop.name);
          }
          current = prop.parent;
        }
        
        return path;
      };
      
      const parentPath = buildPath(parentIndex);
      
      try {
        const currentSchema = JSON.parse(responseSchemaJson);
        
        let targetObject = currentSchema;
        for (const pathSegment of parentPath) {
          if (!targetObject.properties) {
            targetObject.properties = {};
          }
          if (!targetObject.properties[pathSegment]) {
            targetObject.properties[pathSegment] = { type: 'object', properties: {} };
          }
          targetObject = targetObject.properties[pathSegment];
        }
        
        if (!targetObject.properties) {
          targetObject.properties = {};
        }
        
        let newPropName = 'new_attribute';
        let counter = 1;
        while (targetObject.properties[newPropName]) {
          newPropName = `new_attribute_${counter}`;
          counter++;
        }
        
        targetObject.properties[newPropName] = {
          type: 'string'
        };
        
        setIsModified(true);
        
        const newJsonString = JSON.stringify(currentSchema, null, 2);
        setResponseSchemaJson(newJsonString);
        
        setTimeout(() => {
          const newProperties = parseSchemaToPropertiesFn(newJsonString);
          const parentName = prev[parentIndex].name;
          const parentLevel = prev[parentIndex].level;
          
          const updatedProperties = newProperties.map(prop => {
            if (prop.name === parentName && prop.level === parentLevel) {
              return { ...prop, collapsed: false };
            }
            return prop;
          });
          
          setResponseSchemaProperties(updatedProperties);
        }, 0);
        
        return prev;
      } catch (error) {
        console.error('Error adding response child property:', error);
        return prev;
      }
    });
  }, [responseSchemaJson, setIsModified, parseSchemaToPropertiesFn]);

  const removeResponseProperty = useCallback((index: number) => {
    setResponseSchemaProperties(prev => {
      const toRemove = new Set([index]);
      
      const findDescendants = (parentIdx: number) => {
        prev.forEach((prop, idx) => {
          if (prop.parent === parentIdx) {
            toRemove.add(idx);
            findDescendants(idx);
          }
        });
      };
      
      findDescendants(index);
      
      const sortedIndices = Array.from(toRemove).sort((a, b) => b - a);
      let newProperties = [...prev];
      
      sortedIndices.forEach(idx => {
        newProperties.splice(idx, 1);
      });
      
      const indexMapping = new Map();
      let newIndex = 0;
      prev.forEach((_, oldIndex) => {
        if (!toRemove.has(oldIndex)) {
          indexMapping.set(oldIndex, newIndex);
          newIndex++;
        }
      });
      
      newProperties = newProperties.map(prop => {
        if (prop.parent !== undefined && indexMapping.has(prop.parent)) {
          return { ...prop, parent: indexMapping.get(prop.parent) };
        } else if (prop.parent !== undefined) {
          return { ...prop, parent: undefined, level: 0 };
        }
        return prop;
      });
      
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateResponsePropertyName = useCallback((index: number, name: string) => {
    setResponseSchemaProperties(prev => {
      const newProperties = [...prev];
      newProperties[index].name = name;
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateResponsePropertyType = useCallback((index: number, type: string) => {
    setResponseSchemaProperties(prev => {
      const newProperties = [...prev];
      newProperties[index].type = type;
      
      if (!['string', 'number', 'boolean'].includes(type)) {
        newProperties[index].required = false;
        newProperties[index].xIdentity = false;
      }
      
      if (type !== 'number' && newProperties[index].validationRule?.type === 'range') {
        delete newProperties[index].validationRule;
      }
      
      if (type === 'number' && newProperties[index].validationRule?.type === 'regex') {
        delete newProperties[index].validationRule;
      }
      
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateResponsePropertyRequired = useCallback((index: number, required: boolean) => {
    setResponseSchemaProperties(prev => {
      const newProperties = [...prev];
      newProperties[index].required = required;
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateResponsePropertyXIdentity = useCallback((index: number, xIdentity: boolean) => {
    setResponseSchemaProperties(prev => {
      const newProperties = [...prev];
      newProperties[index].xIdentity = xIdentity;
      if (xIdentity) {
        newProperties[index].required = true;
      }
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateResponseValidationRuleType = useCallback((index: number, ruleType: string) => {
    setResponseSchemaProperties(prev => {
      const newProperties = [...prev];
      if (ruleType === '') {
        delete newProperties[index].validationRule;
      } else {
        newProperties[index].validationRule = { type: ruleType, value: '' };
      }
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateResponseValidationRuleValue = useCallback((index: number, value: string) => {
    setResponseSchemaProperties(prev => {
      const newProperties = [...prev];
      if (newProperties[index].validationRule) {
        newProperties[index].validationRule!.value = value;
      }
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateResponseValidationRuleElementType = useCallback((index: number, elementType: string) => {
    setResponseSchemaProperties(prev => {
      const newProperties = [...prev];
      if (newProperties[index].validationRule) {
        newProperties[index].validationRule!.elementType = elementType;
      }
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateResponseValidationRuleValues = useCallback((index: number, values: string) => {
    setResponseSchemaProperties(prev => {
      const newProperties = [...prev];
      if (newProperties[index].validationRule) {
        newProperties[index].validationRule!.values = values;
      }
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateResponseValidationRuleMin = useCallback((index: number, min: string) => {
    setResponseSchemaProperties(prev => {
      const newProperties = [...prev];
      if (newProperties[index].validationRule) {
        newProperties[index].validationRule!.min = min;
      }
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateResponseValidationRuleMax = useCallback((index: number, max: string) => {
    setResponseSchemaProperties(prev => {
      const newProperties = [...prev];
      if (newProperties[index].validationRule) {
        newProperties[index].validationRule!.max = max;
      }
      return newProperties;
    });
    setIsModified(true);
  }, [setIsModified]);

  const buildResponseSchemaFromProperties = useCallback((properties?: SchemaProperty[]): any => {
    const propsToUse = properties || responseSchemaProperties;
    if (propsToUse.length === 0) {
      return {};
    }

    const buildObjectProperties = (properties: SchemaProperty[], level: number, parentIndex?: number): any => {
      const obj: any = {
        type: 'object',
        properties: {},
        required: []
      };

      properties.forEach((prop, currentIndex) => {
        if (prop.level === level && (parentIndex === undefined || prop.parent === parentIndex)) {
          if (!prop.name.trim()) return;

          const propertySchema: any = {};

          switch (prop.type) {
            case 'string':
              propertySchema.type = 'string';
              break;
            case 'number':
              propertySchema.type = 'number';
              break;
            case 'boolean':
              propertySchema.type = 'boolean';
              break;
            case 'array':
              propertySchema.type = 'array';
              propertySchema.items = { type: 'string' };
              break;
            case 'object':
              const hasChildren = properties.some(p => p.parent === currentIndex);
              if (hasChildren) {
                const childSchema = buildObjectProperties(properties, level + 1, currentIndex);
                propertySchema.type = 'object';
                propertySchema.properties = childSchema.properties;
                if (childSchema.required.length > 0) {
                  propertySchema.required = childSchema.required;
                }
              } else {
                propertySchema.type = 'object';
                propertySchema.properties = {};
              }
              break;
          }

          if (prop.validationRule) {
            switch (prop.validationRule.type) {
              case 'equals':
                if (prop.type === 'number') {
                  const numericValue = parseFloat(prop.validationRule.value);
                  propertySchema.const = isNaN(numericValue) ? prop.validationRule.value : numericValue;
                } else {
                  propertySchema.const = prop.validationRule.value;
                }
                break;
              case 'contains':
                const escapedResponseContainsValue = prop.validationRule.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                propertySchema.pattern = `.*${escapedResponseContainsValue}.*`;
                break;
              case 'regex':
                propertySchema.pattern = prop.validationRule.value;
                break;
              case 'range':
                if (prop.type === 'number' && prop.validationRule.value.includes('-')) {
                  const parts = prop.validationRule.value.split('-');
                  const minStr = parts[0].trim();
                  const maxStr = parts[1] ? parts[1].trim() : '';
                  
                  if (minStr) {
                    const min = parseFloat(minStr);
                    if (!isNaN(min)) propertySchema.minimum = min;
                  }
                  if (maxStr) {
                    const max = parseFloat(maxStr);
                    if (!isNaN(max)) propertySchema.maximum = max;
                  }
                }
                break;
              case 'array-all':
                propertySchema['x-array-validation'] = {
                  type: 'all',
                  elementType: prop.validationRule.elementType || 'string',
                  values: prop.validationRule.values ? prop.validationRule.values.split(',').map(v => v.trim()).filter(v => v) : undefined
                };
                break;
              case 'array-any':
                propertySchema['x-array-validation'] = {
                  type: 'any',
                  elementType: prop.validationRule.elementType || 'string',
                  values: prop.validationRule.values ? prop.validationRule.values.split(',').map(v => v.trim()).filter(v => v) : undefined
                };
                break;
              case 'array-length':
                propertySchema['x-array-validation'] = {
                  type: 'length',
                  min: prop.validationRule.min ? parseInt(prop.validationRule.min) : undefined,
                  max: prop.validationRule.max ? parseInt(prop.validationRule.max) : undefined
                };
                break;
            }
          }

          if (prop.xIdentity) {
            propertySchema['x-identity'] = true;
          }

          obj.properties[prop.name] = propertySchema;

          if (prop.required) {
            obj.required.push(prop.name);
          }
        }
      });

      return obj;
    };

    const rootSchema = buildObjectProperties(propsToUse, 0);
    return rootSchema;
  }, [responseSchemaProperties]);

  const updateResponseSchemaJson = useCallback((properties?: SchemaProperty[]) => {
    const propsToUse = properties || responseSchemaProperties;
    if (propsToUse.length === 0) {
      setResponseSchemaJson('');
      return;
    }

    const schema = buildResponseSchemaFromProperties(propsToUse);
    const jsonString = JSON.stringify(schema, null, 2);
    setResponseSchemaJson(jsonString);
    setIsModified(true);
  }, [responseSchemaProperties, buildResponseSchemaFromProperties, setIsModified]);

  const updateResponseSchemaJsonFromProperties = useCallback((properties?: SchemaProperty[]) => {
    updateResponseSchemaJson(properties);
  }, [updateResponseSchemaJson]);

  const parseResponseSchemaPreservingCollapsedState = useCallback((schemaText: string): SchemaProperty[] => {
    const newProperties = parseSchemaToPropertiesFn(schemaText);
    
    if (responseSchemaProperties.length > 0) {
      const existingCollapsedState = new Map<string, boolean>();
      
      const buildCollapsedMap = (props: SchemaProperty[], path = '') => {
        props.forEach(prop => {
          const fullPath = path ? `${path}.${prop.name}` : prop.name;
          if (prop.collapsed !== undefined) {
            existingCollapsedState.set(fullPath, prop.collapsed);
          }
        });
      };
      
      buildCollapsedMap(responseSchemaProperties);
      
      const applyCollapsedState = (props: SchemaProperty[], path = '') => {
        props.forEach(prop => {
          const fullPath = path ? `${path}.${prop.name}` : prop.name;
          if (existingCollapsedState.has(fullPath)) {
            prop.collapsed = existingCollapsedState.get(fullPath);
          }
        });
      };
      
      applyCollapsedState(newProperties);
    }
    
    return newProperties;
  }, [responseSchemaProperties, parseSchemaToPropertiesFn]);

  const syncResponseVisualWithJson = useCallback(() => {
    if (responseSchemaJson) {
      try {
        const newProperties = parseSchemaToPropertiesFn(responseSchemaJson);
        setResponseSchemaProperties(newProperties);
        setIsModified(true);
      } catch (e) {
        // Failed to sync response visual editor with JSON
      }
    }
  }, [responseSchemaJson, parseSchemaToPropertiesFn, setIsModified]);

  const expandAllResponseProperties = useCallback(() => {
    setResponseSchemaProperties(prev => prev.map(prop => ({ ...prop, collapsed: false })));
  }, []);

  const collapseAllResponseProperties = useCallback(() => {
    setResponseSchemaProperties(prev => prev.map(prop => ({ ...prop, collapsed: true })));
  }, []);

  const syncResponseVisualToJson = useCallback(() => {
    updateResponseSchemaJsonFromProperties(responseSchemaProperties);
  }, [responseSchemaProperties, updateResponseSchemaJsonFromProperties]);

  const syncResponseJsonToVisual = useCallback(() => {
    syncResponseVisualWithJson();
  }, [syncResponseVisualWithJson]);

  // Response metadata functions
  const buildMetadataJsonFromProperties = useCallback((properties: SchemaProperty[]): any => {
    const result: any = {};
    
    properties.forEach((prop, index) => {
      if (prop.level === 0 && prop.name) {
        const { parsedValue } = parseValueType(prop.value || '');
        result[prop.name] = parsedValue;
      }
    });
    
    return result;
  }, []);

  const parseValueType = useCallback((value: string): { type: string; parsedValue: any } => {
    if (!value || value.trim() === '') {
      return { type: 'string', parsedValue: '' };
    }
    
    const trimmed = value.trim();
    
    if (trimmed === 'true') {
      return { type: 'boolean', parsedValue: true };
    }
    if (trimmed === 'false') {
      return { type: 'boolean', parsedValue: false };
    }
    
    if (/^-?\d+$/.test(trimmed)) {
      return { type: 'number', parsedValue: parseInt(trimmed, 10) };
    }
    
    if (/^-?\d+\.\d+$/.test(trimmed)) {
      return { type: 'number', parsedValue: parseFloat(trimmed) };
    }
    
    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) ||
        (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      try {
        const parsed = JSON.parse(trimmed);
        return { type: Array.isArray(parsed) ? 'array' : 'object', parsedValue: parsed };
      } catch {
        return { type: 'string', parsedValue: value };
      }
    }
    
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
      try {
        const parsed = JSON.parse(trimmed);
        return { type: 'string', parsedValue: parsed };
      } catch {
        return { type: 'string', parsedValue: value };
      }
    }
    
    return { type: 'string', parsedValue: value };
  }, []);

  const updateResponseMetadataJsonFromProperties = useCallback((properties?: SchemaProperty[]) => {
    const propsToUse = properties || responseMetadataSchemaProperties;
    const jsonObj = buildMetadataJsonFromProperties(propsToUse);
    setResponseMetadataSchemaJson(JSON.stringify(jsonObj, null, 2));
    setIsModified(true);
  }, [responseMetadataSchemaProperties, buildMetadataJsonFromProperties, setIsModified]);

  const addResponseMetadataProperty = useCallback(() => {
    setResponseMetadataSchemaProperties(prev => [...prev, {
      name: '',
      type: 'string',
      required: false,
      xIdentity: false,
      level: 0,
      collapsed: false
    }]);
    setIsModified(true);
  }, [setIsModified]);

  const addResponseMetadataChildProperty = useCallback((parentIndex: number) => {
    setResponseMetadataSchemaProperties(prev => {
      const newProps = [...prev];
      const parentProp = newProps[parentIndex];
      
      if (parentProp.type !== 'object') {
        parentProp.type = 'object';
      }
      
      const newProp = {
        name: '',
        type: 'string',
        required: false,
        xIdentity: false,
        level: parentProp.level + 1,
        parent: parentIndex,
        collapsed: false
      };
      
      let insertIndex = parentIndex + 1;
      while (insertIndex < newProps.length && newProps[insertIndex].level > parentProp.level) {
        insertIndex++;
      }
      
      newProps.splice(insertIndex, 0, newProp);
      return newProps;
    });
    setIsModified(true);
  }, [setIsModified]);

  const removeResponseMetadataProperty = useCallback((index: number) => {
    setResponseMetadataSchemaProperties(prev => {
      const newProps = [...prev];
      const prop = newProps[index];
      
      const toRemove = [index];
      for (let i = index + 1; i < newProps.length; i++) {
        if (newProps[i].level > prop.level) {
          toRemove.push(i);
        } else {
          break;
        }
      }
      
      const filtered = newProps.filter((_, i) => !toRemove.includes(i));
      
      updateResponseMetadataJsonFromProperties(filtered);
      return filtered;
    });
  }, [updateResponseMetadataJsonFromProperties]);

  const toggleResponseMetadataPropertyCollapsed = useCallback((index: number) => {
    setResponseMetadataSchemaProperties(prev => {
      const newProps = [...prev];
      newProps[index].collapsed = !newProps[index].collapsed;
      return newProps;
    });
  }, []);

  const isMetadataChildVisible = useCallback((property: SchemaProperty, parentIndex?: number): boolean => {
    if (parentIndex === undefined) return true;
    
    const parent = responseMetadataSchemaProperties[parentIndex];
    if (!parent) return false;
    
    if (parent.collapsed) return false;
    
    if (parent.parent !== undefined) {
      return isMetadataChildVisible(parent, parent.parent);
    }
    
    return true;
  }, [responseMetadataSchemaProperties]);

  const updateResponseMetadataPropertyName = useCallback((index: number, name: string) => {
    setResponseMetadataSchemaProperties(prev => {
      const newProps = [...prev];
      newProps[index].name = name;
      return newProps;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateResponseMetadataPropertyType = useCallback((index: number, type: string) => {
    setResponseMetadataSchemaProperties(prev => {
      const newProps = [...prev];
      newProps[index].type = type;
      return newProps;
    });
    setIsModified(true);
  }, [setIsModified]);

  const updateResponseMetadataPropertyValue = useCallback((index: number, value: string) => {
    setResponseMetadataSchemaProperties(prev => {
      const newProps = [...prev];
      (newProps[index] as any).value = value;
      return newProps;
    });
    setIsModified(true);
  }, [setIsModified]);

  const syncResponseMetadataVisualToJson = useCallback(() => {
    updateResponseMetadataJsonFromProperties(responseMetadataSchemaProperties);
  }, [responseMetadataSchemaProperties, updateResponseMetadataJsonFromProperties]);

  const syncResponseMetadataJsonToVisual = useCallback(() => {
    if (responseMetadataSchemaJson && responseMetadataSchemaJson !== '{}') {
      try {
        const parsed = JSON.parse(responseMetadataSchemaJson);
        const props: SchemaProperty[] = [];
        
        const parseObject = (obj: any, level: number, parentIndex?: number) => {
          Object.keys(obj).forEach(key => {
            const val = obj[key];
            let type = 'string';
            let valueStr = '';
            
            if (typeof val === 'number') {
              type = 'number';
              valueStr = String(val);
            } else if (typeof val === 'boolean') {
              type = 'boolean';
              valueStr = String(val);
            } else if (Array.isArray(val)) {
              type = 'array';
              valueStr = JSON.stringify(val);
            } else if (typeof val === 'object' && val !== null) {
              type = 'object';
            } else {
              type = 'string';
              valueStr = String(val);
            }
            
            const propIndex = props.length;
            props.push({
              name: key,
              type,
              required: false,
              xIdentity: false,
              level,
              parent: parentIndex,
              collapsed: false,
              value: valueStr
            });
            
            if (type === 'object') {
              parseObject(val, level + 1, propIndex);
            }
          });
        };
        
        parseObject(parsed, 0);
        setResponseMetadataSchemaProperties(props);
        setIsModified(true);
      } catch (e) {
        console.error('Failed to sync response metadata JSON to visual:', e);
      }
    }
  }, [responseMetadataSchemaJson, setIsModified]);

  return {
    // State
    responseSchemaProperties,
    setResponseSchemaProperties,
    responseSchemaJson,
    setResponseSchemaJson,
    responseSchemaJsonValid,
    responseSchemaValidationErrors,
    showResponseJson,
    setShowResponseJson,
    responseMetadataSchemaProperties,
    setResponseMetadataSchemaProperties,
    responseMetadataSchemaJson,
    setResponseMetadataSchemaJson,
    responseMetadataJsonValid,
    showResponseMetadataJson,
    setShowResponseMetadataJson,
    
    // Response schema property functions
    toggleResponsePropertyCollapsed,
    isResponseChildVisible,
    addResponseSchemaProperty,
    addResponseChildProperty,
    removeResponseProperty,
    updateResponsePropertyName,
    updateResponsePropertyType,
    updateResponsePropertyRequired,
    updateResponsePropertyXIdentity,
    updateResponseValidationRuleType,
    updateResponseValidationRuleValue,
    updateResponseValidationRuleElementType,
    updateResponseValidationRuleValues,
    updateResponseValidationRuleMin,
    updateResponseValidationRuleMax,
    updateResponseSchemaJsonFromProperties,
    updateResponseSchemaJson,
    parseResponseSchemaPreservingCollapsedState,
    expandAllResponseProperties,
    collapseAllResponseProperties,
    syncResponseVisualToJson,
    syncResponseJsonToVisual,
    syncResponseVisualWithJson,
    
    // Response metadata functions
    addResponseMetadataProperty,
    addResponseMetadataChildProperty,
    removeResponseMetadataProperty,
    updateResponseMetadataPropertyName,
    updateResponseMetadataPropertyType,
    updateResponseMetadataPropertyValue,
    toggleResponseMetadataPropertyCollapsed,
    isMetadataChildVisible,
    updateResponseMetadataJsonFromProperties,
    syncResponseMetadataVisualToJson,
    syncResponseMetadataJsonToVisual,
    
    // Shared utilities
    parseValueType,
    parseSchemaToProperties: parseSchemaToPropertiesFn,
    buildResponseSchemaFromProperties,
  };
};
